import { useState } from "react";
import { Pencil, Undo2, X, ArrowRightLeft } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AuthImage } from "@/components/damages/AuthImage";
import { fmtDate } from "@/lib/format";

const km = (n) => (n == null ? "—" : `${n.toLocaleString("it-IT")} km`);
const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-mono-num text-base font-semibold text-slate-900" data-testid={testId}>{value}</p>
  </div>
);

const Phase = ({ title, date, notes, photos, onZoom, testId }) => (
  <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid={testId}>
    <p className="text-sm font-semibold text-slate-900">{title} <span className="font-normal text-slate-500">{date ? `· ${fmtDate(date)}` : ""}</span></p>
    <p className="whitespace-pre-wrap text-sm text-slate-700">{notes || <span className="text-slate-400">Nessun danno segnalato</span>}</p>
    {photos.length ? (
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p) => <AuthImage key={p.id} photoId={p.id} base="/rentals/photos" className="h-20 w-full cursor-zoom-in rounded-lg" onClick={() => onZoom(p.id)} testId={`${testId}-photo-${p.id}`} />)}
      </div>
    ) : <p className="text-xs text-slate-400">Nessuna foto</p>}
  </div>
);

export const RentalView = ({ rental, onClose, onEdit, onReturn }) => {
  const [zoom, setZoom] = useState(null);
  const r = rental;
  const days = r && Math.round((new Date(r.return_date || new Date().toISOString().slice(0, 10)) - new Date(r.pickup_date)) / 86400000) + 1;
  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="rental-view-dialog">
        {r && <>
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <span className="font-mono-num rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-sm">{r.plate}</span> {r.model}
              <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${r.status === "ATTIVO" ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-200 bg-slate-100 text-slate-600"}`}>{r.status === "ATTIVO" ? "In noleggio" : "Riconsegnato"}</span>
            </DialogTitle>
            <DialogDescription>{r.provider}{r.location && ` · ${r.location}`} · {r.station_name}</DialogDescription>
          </DialogHeader>
          {r.transfer_station_id && <p className="flex items-center gap-1 text-sm font-medium text-violet-700"><ArrowRightLeft className="h-4 w-4" /> Trasferito a {r.transfer_station_name}</p>}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Km iniziali" value={km(r.km_start)} />
            <Stat label="Km finali" value={km(r.km_end)} />
            <Stat label="Km percorsi" value={km(r.km_driven)} testId="rental-view-km" />
            <Stat label={r.status === "ATTIVO" ? "Giorni finora" : "Giorni"} value={days} testId="rental-view-days" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Phase title="Ritiro" date={r.pickup_date} notes={r.damage_notes} photos={r.photos.pickup} onZoom={setZoom} testId="rental-view-pickup" />
            <Phase title="Riconsegna" date={r.return_date} notes={r.status === "ATTIVO" ? "Non ancora riconsegnato" : r.return_notes} photos={r.photos.return} onZoom={setZoom} testId="rental-view-return" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose} data-testid="rental-view-close">Chiudi</Button>
            <Button variant="outline" onClick={() => onEdit(r)} data-testid="rental-view-edit"><Pencil className="mr-2 h-4 w-4" /> Modifica</Button>
            <Button onClick={() => onReturn(r)} data-testid="rental-view-return-button"><Undo2 className="mr-2 h-4 w-4" /> {r.status === "ATTIVO" ? "Riconsegna" : "Modifica riconsegna"}</Button>
          </DialogFooter>
          {zoom && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4" onClick={() => setZoom(null)} data-testid="rental-view-zoom">
              <button className="absolute right-4 top-4 rounded-full bg-white/90 p-2"><X className="h-5 w-5" /></button>
              <AuthImage photoId={zoom} base="/rentals/photos" className="max-h-[90vh] max-w-full rounded-lg object-contain" />
            </div>
          )}
        </>}
      </DialogContent>
    </Dialog>
  );
};
